/**
 * Booking Office Sales — daily ticket takings by booking office / channel.
 *
 * The ticket-sales mirror of SalesDashboard (Retail & Tea Rooms). Reads
 * /api/dashboards/booking-offices/sales?fiscalYear=… (one NYMR season of daily
 * rows: date × location × category × channel × gross/net/vat/items/txns) and
 * renders it as a single matrix — booking offices across, time down. Pick a
 * month → daily rows; "Whole season" → monthly rows. A season summary (by
 * location) sits on top.
 *
 * Tickets are the "products": clicking a figure (Breakdown mode) splits it by
 * ticket type; a "Top ticket types" table ranks the biggest sellers. Category
 * pills (Travel / Special Events / Pullman / …) filter the whole view.
 *
 * NYMR season runs 1 Mar – end Feb, labelled by the end year ("2026/27").
 * Attribution is by operating/travel day, Completed sales only. Headline is Net
 * (ex-VAT); a Net/Gross toggle switches every figure.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import writeXlsxFile from "write-excel-file/browser";
import "./sales.css";

type Measure = "Net" | "Gross";

interface BORow {
  SaleDate: string; // YYYY-MM-DD
  Location: string;
  Category: string;
  Channel: string;
  Gross: number;
  Net: number;
  VAT: number;
  Items: number;
  Transactions: number;
}

interface BOResponse {
  fiscalYear: number;
  fiscalYearLabel: string;
  years: { fiscalYear: number; label: string }[];
  last_refresh: string | null;
  rows: BORow[];
}

interface BreakdownItem {
  Analysis: string | null;
  Category: string | null;
  Gross: number;
  Net: number;
  Items: number;
}

interface TicketRow {
  plu: string;
  item: string;
  analysis: string | null;
  category: string | null;
  qty: number;
  gross: number;
  net: number;
}
interface TopTicketsResp {
  from: string;
  to: string;
  total_net: number;
  rows: TicketRow[];
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const gbp0 = new Intl.NumberFormat("en-GB", { maximumFractionDigits: 0 });
const gbp2 = new Intl.NumberFormat("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
function money0(n: number): string {
  return `£${gbp0.format(Math.round(n))}`;
}
function money(n: number, roundUp: boolean): string {
  return roundUp ? money0(n) : `£${gbp2.format(n)}`;
}

// Booking offices in railway-line order, then the non-counter channels.
const LOCATION_ORDER = ["Pickering", "Grosmont", "Goathland", "Whitby", "Customer Services", "Online", "Finance", "Other"];
function locationRank(loc: string): number {
  const i = LOCATION_ORDER.indexOf(loc);
  return i === -1 ? LOCATION_ORDER.length : i;
}
// Ticket groups in a sensible reading order.
const CATEGORY_ORDER = ["Travel", "Special Events", "Pullman", "Experiences", "Stays", "Membership & Donations", "Other"];
function categoryRank(cat: string): number {
  const i = CATEGORY_ORDER.indexOf(cat);
  return i === -1 ? CATEGORY_ORDER.length : i;
}

function monthLabel(key: string): string {
  const [y, m] = key.split("-").map(Number);
  return `${MONTHS[m - 1]} ${y}`;
}
function dayLabel(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  const dow = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d.getUTCDay()];
  const [, m, dd] = iso.split("-").map(Number);
  return `${dow} ${dd} ${MONTHS[m - 1]}`;
}
function lastDayIso(ym: string): string {
  const [y, m] = ym.split("-").map(Number);
  const day = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return `${ym}-${String(day).padStart(2, "0")}`;
}
function currentFiscalYear(): number {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth() + 1;
  return m >= 3 ? y + 1 : y;
}
function prettyDateTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false,
  });
}

interface Matrix {
  visible: string[];
  grid: { key: string; label: string; cells: number[]; rowTotal: number }[];
  colTotals: number[];
  grandTotal: number;
}

// ---------------------------------------------------------------------------
export function BookingOfficeSalesDashboard() {
  const [fiscalYear, setFiscalYear] = useState<number>(() => currentFiscalYear());
  const [data, setData] = useState<BOResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [measure, setMeasure] = useState<Measure>("Net");
  const [roundUp, setRoundUp] = useState(true);
  const [breakdownMode, setBreakdownMode] = useState(false);
  const [monthKey, setMonthKey] = useState<string>("");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [hiddenLoc, setHiddenLoc] = useState<Set<string>>(new Set());
  const [hiddenCat, setHiddenCat] = useState<Set<string>>(new Set());

  const [bd, setBd] = useState<{ title: string; loading: boolean; error: string | null; rows: BreakdownItem[]; total: number } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(`/api/dashboards/booking-offices/sales?fiscalYear=${fiscalYear}`, { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) {
          const b = await res.json().catch(() => ({}));
          throw new Error(b.error || `Request failed (${res.status})`);
        }
        return res.json() as Promise<BOResponse>;
      })
      .then((d) => {
        if (cancelled) return;
        setData(d);
        if (d.fiscalYear !== fiscalYear) setFiscalYear(d.fiscalYear);
        setHiddenLoc(new Set());
        setHiddenCat(new Set());
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
        setData(null);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [fiscalYear]);

  const rows = data?.rows ?? [];

  const months = useMemo(() => {
    const s = new Set<string>();
    for (const r of rows) s.add(r.SaleDate.slice(0, 7));
    return [...s].sort();
  }, [rows]);

  const seasonBounds = useMemo(() => {
    if (rows.length === 0) return { min: undefined as string | undefined, max: undefined as string | undefined };
    let min = rows[0].SaleDate;
    let max = rows[0].SaleDate;
    for (const r of rows) {
      if (r.SaleDate < min) min = r.SaleDate;
      if (r.SaleDate > max) max = r.SaleDate;
    }
    return { min, max };
  }, [rows]);

  useEffect(() => {
    if (months.length === 0) { setMonthKey(""); return; }
    const nowKey = new Date().toISOString().slice(0, 7);
    setMonthKey(months.includes(nowKey) ? nowKey : months[months.length - 1]);
    setDateFrom("");
    setDateTo("");
  }, [months]);

  const usingRange = Boolean(dateFrom && dateTo && dateFrom <= dateTo);
  const dayGrain = usingRange || monthKey !== "";

  const scopedRows = useMemo(() => {
    return rows.filter((r) => {
      if (usingRange) return r.SaleDate >= dateFrom && r.SaleDate <= dateTo;
      if (monthKey) return r.SaleDate.slice(0, 7) === monthKey;
      return true;
    });
  }, [rows, usingRange, dateFrom, dateTo, monthKey]);

  const allLocations = useMemo(() => {
    const s = new Set<string>();
    for (const r of rows) s.add(r.Location);
    return [...s].sort((a, b) => locationRank(a) - locationRank(b) || a.localeCompare(b));
  }, [rows]);

  const allCategories = useMemo(() => {
    const s = new Set<string>();
    for (const r of rows) s.add(r.Category);
    return [...s].sort((a, b) => categoryRank(a) - categoryRank(b) || a.localeCompare(b));
  }, [rows]);

  const visibleCats = useMemo(() => allCategories.filter((c) => !hiddenCat.has(c)), [allCategories, hiddenCat]);
  // Category filter passed to breakdown / top-tickets — explicit only when some
  // are hidden, so the split reconciles to the on-screen figure.
  const catFilter = hiddenCat.size > 0 ? visibleCats.join("|") : "";

  // Single matrix: buckets (rows) × visible locations, honouring the location +
  // category filters.
  const matrix = useMemo<Matrix>(() => {
    const visible = allLocations.filter((l) => !hiddenLoc.has(l));
    const bucketMap = new Map<string, Map<string, number>>();
    for (const r of scopedRows) {
      if (hiddenLoc.has(r.Location)) continue;
      if (hiddenCat.has(r.Category)) continue;
      const bk = dayGrain ? r.SaleDate : r.SaleDate.slice(0, 7);
      if (!bucketMap.has(bk)) bucketMap.set(bk, new Map());
      const lm = bucketMap.get(bk)!;
      lm.set(r.Location, (lm.get(r.Location) ?? 0) + r[measure]);
    }
    const buckets = [...bucketMap.keys()].sort();
    const grid = buckets.map((bk) => {
      const lm = bucketMap.get(bk)!;
      const cells = visible.map((loc) => lm.get(loc) ?? 0);
      const rowTotal = cells.reduce((a, b) => a + b, 0);
      return { key: bk, label: dayGrain ? dayLabel(bk) : monthLabel(bk), cells, rowTotal };
    });
    const colTotals = visible.map((_, i) => grid.reduce((a, g) => a + g.cells[i], 0));
    const grandTotal = colTotals.reduce((a, b) => a + b, 0);
    return { visible, grid, colTotals, grandTotal };
  }, [scopedRows, allLocations, hiddenLoc, hiddenCat, dayGrain, measure]);

  // Season summary — by location (whole season, chosen measure, honouring the
  // category filter so the tiles track the pills).
  const summary = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of rows) {
      if (hiddenCat.has(r.Category)) continue;
      m.set(r.Location, (m.get(r.Location) ?? 0) + r[measure]);
    }
    const items = [...m.entries()]
      .map(([location, value]) => ({ location, value }))
      .sort((a, b) => locationRank(a.location) - locationRank(b.location) || a.location.localeCompare(b.location));
    const total = items.reduce((a, b) => a + b.value, 0);
    const max = items.reduce((a, b) => Math.max(a, b.value), 0);
    return { items, total, max };
  }, [rows, measure, hiddenCat]);

  const toggleLoc = (loc: string) => {
    setHiddenLoc((prev) => {
      const next = new Set(prev);
      if (next.has(loc)) next.delete(loc); else next.add(loc);
      return next;
    });
  };
  const toggleCat = (cat: string) => {
    setHiddenCat((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat); else next.add(cat);
      return next;
    });
  };

  const rangeLabel = usingRange
    ? `${dayLabel(dateFrom)} – ${dayLabel(dateTo)}`
    : monthKey ? monthLabel(monthKey) : `Season ${data?.fiscalYearLabel ?? ""}`;

  const effRange = useMemo(() => {
    if (usingRange) return { from: dateFrom, to: dateTo };
    if (monthKey) return { from: `${monthKey}-01`, to: lastDayIso(monthKey) };
    return { from: seasonBounds.min ?? "", to: seasonBounds.max ?? "" };
  }, [usingRange, dateFrom, dateTo, monthKey, seasonBounds]);

  const onExcel = useCallback(() => {
    if (!data) return;
    void exportExcel(data, { measure, matrix, summary, rangeLabel });
  }, [data, measure, matrix, summary, rangeLabel]);
  const onPdf = useCallback(() => {
    if (!data) return;
    exportPdf(data, { measure, matrix, rangeLabel, roundUp });
  }, [data, measure, matrix, rangeLabel, roundUp]);

  const openBreakdown = useCallback(
    (locations: string, from: string, to: string, title: string) => {
      setBd({ title, loading: true, error: null, rows: [], total: 0 });
      const qs = new URLSearchParams({ from, to });
      if (locations) qs.set("locations", locations);
      if (catFilter) qs.set("categories", catFilter);
      fetch(`/api/dashboards/booking-offices/breakdown?${qs.toString()}`, { credentials: "include" })
        .then(async (res) => {
          if (!res.ok) {
            const b = await res.json().catch(() => ({}));
            throw new Error(b.error || `Request failed (${res.status})`);
          }
          return res.json() as Promise<{ rows: BreakdownItem[] }>;
        })
        .then((r) => {
          const brows = r.rows ?? [];
          const total = brows.reduce((a, x) => a + x[measure === "Gross" ? "Gross" : "Net"], 0);
          setBd({ title, loading: false, error: null, rows: brows, total });
        })
        .catch((err) => setBd({ title, loading: false, error: err instanceof Error ? err.message : String(err), rows: [], total: 0 }));
    },
    [measure, catFilter]
  );

  const hasData = !loading && !error && rows.length > 0;

  const bucketRange = (bucketKey: string | null): { from: string; to: string } => {
    if (bucketKey) return dayGrain ? { from: bucketKey, to: bucketKey } : { from: `${bucketKey}-01`, to: lastDayIso(bucketKey) };
    const first = matrix.grid[0]?.key;
    const last = matrix.grid[matrix.grid.length - 1]?.key;
    if (!first || !last) return { from: "1900-01-01", to: "1900-01-01" };
    return dayGrain ? { from: first, to: last } : { from: `${first}-01`, to: lastDayIso(last) };
  };
  const clickCell = (locations: string, bucketKey: string | null, label: string) => {
    if (!breakdownMode) return;
    const { from, to } = bucketRange(bucketKey);
    openBreakdown(locations, from, to, label);
  };

  return (
    <main className="app-main sales">
      <div className="sales-head">
        <div>
          <p className="sales-sub">
            Daily ticket takings by booking office and channel, from the EPOS/booking system.
            Totals shown as <strong>{measure === "Net" ? "Net (ex-VAT)" : "Gross (incl VAT)"}</strong>.
            {breakdownMode ? " Click any figure for its ticket-type breakdown." : ""}
          </p>
        </div>
        <div className="sales-refresh-note">
          Last refreshed {prettyDateTime(data?.last_refresh ?? null)}. Updates automatically each morning.
        </div>
      </div>

      {/* Season summary — by location */}
      <section className="sales-summary" aria-label="Season booking office summary">
        <div className="sales-summary-head">
          <h2>Ticket sales — season {data?.fiscalYearLabel ?? ""} by location</h2>
          <span className="sales-summary-total">{money(summary.total, roundUp)}</span>
        </div>
        <div className="sales-summary-tiles">
          {summary.items.map((it) => (
            <div className="sales-tile" key={it.location} title={`${it.location}: ${money(it.value, roundUp)}`}>
              <div className="sales-tile-loc">{it.location}</div>
              <div className="sales-tile-val">{money(it.value, roundUp)}</div>
              <div className="sales-tile-bar">
                <span style={{ width: `${summary.max > 0 ? (it.value / summary.max) * 100 : 0}%` }} />
              </div>
            </div>
          ))}
          {summary.items.length === 0 && <div className="sales-empty">No ticket sales in this season yet.</div>}
        </div>
      </section>

      {/* Controls */}
      <div className="sales-toolbar">
        <label className="sales-field">
          <span>Season</span>
          <select value={fiscalYear} onChange={(e) => setFiscalYear(Number(e.target.value))}>
            {(data?.years ?? [{ fiscalYear, label: "" }]).map((y) => (
              <option key={y.fiscalYear} value={y.fiscalYear}>{y.label || y.fiscalYear}</option>
            ))}
          </select>
        </label>
        <label className="sales-field">
          <span>Month</span>
          <select
            value={usingRange ? "__range__" : monthKey}
            onChange={(e) => {
              setDateFrom("");
              setDateTo("");
              setMonthKey(e.target.value === "__range__" ? "" : e.target.value);
            }}
          >
            <option value="">Whole season</option>
            {months.map((m) => (<option key={m} value={m}>{monthLabel(m)}</option>))}
            {usingRange && <option value="__range__">Custom range</option>}
          </select>
        </label>
        <label className="sales-field">
          <span>From</span>
          <input type="date" value={dateFrom} min={seasonBounds.min} max={dateTo || seasonBounds.max} onChange={(e) => setDateFrom(e.target.value)} />
        </label>
        <label className="sales-field">
          <span>To</span>
          <input type="date" value={dateTo} min={dateFrom || seasonBounds.min} max={seasonBounds.max} onChange={(e) => setDateTo(e.target.value)} />
        </label>
        {usingRange && (
          <button type="button" className="sales-clear" onClick={() => { setDateFrom(""); setDateTo(""); }}>Clear range</button>
        )}
        <div className="sales-measure" role="group" aria-label="Sales measure">
          {(["Net", "Gross"] as Measure[]).map((m) => (
            <button key={m} type="button" className={"sales-measure-btn" + (measure === m ? " active" : "")} onClick={() => setMeasure(m)}>
              {m === "Net" ? "Net (ex VAT)" : "Gross (inc VAT)"}
            </button>
          ))}
        </div>
        <button
          type="button"
          className={"sales-pill sales-round" + (roundUp ? "" : " off")}
          onClick={() => setRoundUp((v) => !v)}
          aria-pressed={roundUp}
          title={roundUp ? "Showing whole pounds — click to show pence (£x.xx)" : "Showing pence (£x.xx) — click to round"}
        >
          Round up
        </button>
        <button
          type="button"
          className={"sales-pill sales-round" + (breakdownMode ? "" : " off")}
          onClick={() => setBreakdownMode((v) => !v)}
          aria-pressed={breakdownMode}
          title="Breakdown mode — click any figure to see the ticket types making it up"
        >
          Breakdown
        </button>
        <span className="sales-spacer" />
        <button type="button" className="sales-export" onClick={onExcel} disabled={!hasData}>⬇ Excel</button>
        <button type="button" className="sales-export" onClick={onPdf} disabled={!hasData}>🖨 PDF</button>
      </div>

      {error ? (
        <div className="sales-empty sales-empty--error">Couldn’t load booking office sales: {error}</div>
      ) : loading ? (
        <div className="sales-empty">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="sales-empty">No booking office sales for this season yet — it may not have refreshed. Try another season.</div>
      ) : (
        <>
          <section className="sales-section">
            <div className="sales-section-head">
              <h2>Booking Office Ticket Sales</h2>
              <span className="sales-section-sub">{rangeLabel} · {money(matrix.grandTotal, roundUp)}</span>
            </div>

            <div className="sales-pillrows">
              {allLocations.length > 0 && (
                <div className="sales-pills" role="group" aria-label="Booking office locations">
                  {allLocations.map((loc) => (
                    <button key={loc} type="button" className={"sales-pill" + (hiddenLoc.has(loc) ? " off" : "")} onClick={() => toggleLoc(loc)} aria-pressed={!hiddenLoc.has(loc)}>
                      {loc}
                    </button>
                  ))}
                </div>
              )}
              {allCategories.length > 0 && (
                <div className="sales-pills sales-classpills" role="group" aria-label="Ticket categories">
                  {allCategories.map((c) => (
                    <button key={c} type="button" className={"sales-pill sales-classpill" + (hiddenCat.has(c) ? " off" : "")} onClick={() => toggleCat(c)} aria-pressed={!hiddenCat.has(c)}>
                      {c}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="sales-table-wrap">
              {matrix.visible.length === 0 ? (
                <div className="sales-empty">All locations hidden — tap a pill to show one.</div>
              ) : matrix.grid.length === 0 ? (
                <div className="sales-empty">No ticket sales in this period.</div>
              ) : (
                <table className={"sales-table" + (breakdownMode ? " breakdown" : "")}>
                  <thead>
                    <tr>
                      <th className="sticky-col">{dayGrain ? "Date" : "Month"}</th>
                      {matrix.visible.map((loc) => <th key={loc} className="ta-right">{loc}</th>)}
                      <th className="ta-right sales-total-col">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {matrix.grid.map((g) => (
                      <tr key={g.key}>
                        <td className="sticky-col">{g.label}</td>
                        {g.cells.map((v, i) => (
                          <td
                            key={i}
                            className={"ta-right" + (breakdownMode ? " clickable" : "")}
                            onClick={breakdownMode ? () => clickCell(matrix.visible[i], g.key, `${matrix.visible[i]} · ${g.label}`) : undefined}
                          >
                            {v ? money(v, roundUp) : "–"}
                          </td>
                        ))}
                        <td
                          className={"ta-right sales-total-col" + (breakdownMode ? " clickable" : "")}
                          onClick={breakdownMode ? () => clickCell(matrix.visible.join("|"), g.key, `All locations · ${g.label}`) : undefined}
                        >
                          {money(g.rowTotal, roundUp)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td className="sticky-col">Total</td>
                      {matrix.colTotals.map((v, i) => (
                        <td
                          key={i}
                          className={"ta-right" + (breakdownMode ? " clickable" : "")}
                          onClick={breakdownMode ? () => clickCell(matrix.visible[i], null, `${matrix.visible[i]} · ${rangeLabel}`) : undefined}
                        >
                          {money(v, roundUp)}
                        </td>
                      ))}
                      <td
                        className={"ta-right sales-total-col" + (breakdownMode ? " clickable" : "")}
                        onClick={breakdownMode ? () => clickCell(matrix.visible.join("|"), null, `All locations · ${rangeLabel}`) : undefined}
                      >
                        {money(matrix.grandTotal, roundUp)}
                      </td>
                    </tr>
                    <tr className="sales-pctrow">
                      <td className="sticky-col">% of total</td>
                      {matrix.colTotals.map((v, i) => (
                        <td key={i} className="ta-right">
                          {matrix.grandTotal ? `${((v / matrix.grandTotal) * 100).toFixed(1)}%` : "–"}
                        </td>
                      ))}
                      <td className="ta-right sales-total-col">{matrix.grandTotal ? "100%" : "–"}</td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          </section>

          <TopTicketsSection from={effRange.from} to={effRange.to} categories={catFilter} roundUp={roundUp} rangeLabel={rangeLabel} />
        </>
      )}

      {bd && (
        <BreakdownModal bd={bd} roundUp={roundUp} measureLabel={measure} onClose={() => setBd(null)} />
      )}
    </main>
  );
}

// ---------------------------------------------------------------------------
// Top ticket types — top-20 tickets by revenue for the window (no cost).
// ---------------------------------------------------------------------------
function TopTicketsSection({ from, to, categories, roundUp, rangeLabel }: { from: string; to: string; categories: string; roundUp: boolean; rangeLabel: string }) {
  const [data, setData] = useState<TopTicketsResp | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!from || !to) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    const qs = new URLSearchParams({ from, to, limit: "20" });
    if (categories) qs.set("categories", categories);
    fetch(`/api/dashboards/booking-offices/top-tickets?${qs.toString()}`, { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) {
          const b = await res.json().catch(() => ({}));
          throw new Error(b.error || `Request failed (${res.status})`);
        }
        return res.json() as Promise<TopTicketsResp>;
      })
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [from, to, categories]);

  return (
    <section className="sales-section">
      <div className="sales-section-head">
        <h2>Top ticket types</h2>
        <span className="sales-section-sub">{rangeLabel} · top 20 by Net revenue</span>
      </div>
      <div className="sales-table-wrap">
        {loading ? (
          <div className="sales-empty">Loading…</div>
        ) : error ? (
          <div className="sales-empty sales-empty--error">{error}</div>
        ) : !data || data.rows.length === 0 ? (
          <div className="sales-empty">No ticket sales in this period (or the cache hasn’t refreshed yet).</div>
        ) : (
          <table className="sales-table">
            <thead>
              <tr>
                <th className="sticky-col">#</th>
                <th>Ticket</th>
                <th>Group</th>
                <th className="ta-right">Qty</th>
                <th className="ta-right">Revenue</th>
                <th className="ta-right">Share</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={r.plu}>
                  <td className="sticky-col">{i + 1}</td>
                  <td>{r.item}</td>
                  <td>{r.category ?? "—"}</td>
                  <td className="ta-right">{r.qty.toLocaleString("en-GB")}</td>
                  <td className="ta-right">{money(r.net, roundUp)}</td>
                  <td className="ta-right">{data.total_net > 0 ? `${((r.net / data.total_net) * 100).toFixed(1)}%` : "–"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Breakdown popup — the ticket-type (Analysis) split behind a clicked figure.
// ---------------------------------------------------------------------------
function BreakdownModal(props: {
  bd: { title: string; loading: boolean; error: string | null; rows: BreakdownItem[]; total: number };
  roundUp: boolean;
  measureLabel: Measure;
  onClose: () => void;
}) {
  const { bd, roundUp, measureLabel, onClose } = props;
  const key = measureLabel === "Gross" ? "Gross" : "Net";
  return (
    <div className="sales-modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="sales-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sales-modal-head">
          <div>
            <div className="sales-modal-title">Breakdown — {bd.title}</div>
            <div className="sales-modal-sub">By ticket type ({measureLabel}) · total {money(bd.total, roundUp)}</div>
          </div>
          <button type="button" className="sales-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="sales-modal-body">
          {bd.loading ? (
            <div className="sales-empty">Loading…</div>
          ) : bd.error ? (
            <div className="sales-empty sales-empty--error">{bd.error}</div>
          ) : bd.rows.length === 0 ? (
            <div className="sales-empty">No detail for this selection.</div>
          ) : (
            <table className="sales-bd-table">
              <thead>
                <tr><th>Ticket type</th><th className="ta-right">{measureLabel}</th><th className="ta-right">Items</th><th className="ta-right">Share</th></tr>
              </thead>
              <tbody>
                {bd.rows.map((r) => {
                  const v = r[key];
                  const share = bd.total !== 0 ? (v / bd.total) * 100 : 0;
                  return (
                    <tr key={r.Analysis ?? "?"}>
                      <td>{r.Analysis ?? "(unclassified)"}</td>
                      <td className="ta-right">{money(v, roundUp)}</td>
                      <td className="ta-right">{r.Items}</td>
                      <td className="ta-right">{share.toFixed(1)}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PDF export — landscape, NYMR-green header, the ticket-sales matrix.
// ---------------------------------------------------------------------------
function exportPdf(
  data: BOResponse,
  ctx: { measure: Measure; matrix: Matrix; rangeLabel: string; roundUp: boolean }
) {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const printedOn = new Date().toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false,
  });
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(11, 61, 46);
  doc.text(`Booking Office Sales — ${data.fiscalYearLabel}`, 10, 12);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(90, 90, 90);
  doc.text(`${ctx.rangeLabel}  ·  ${ctx.measure} (${ctx.measure === "Net" ? "ex VAT" : "inc VAT"})  ·  printed ${printedOn}`, 10, 17);

  const m = ctx.matrix;
  if (m.visible.length > 0 && m.grid.length > 0) {
    const head = [["Date/Month", ...m.visible, "Total"]];
    const body = m.grid.map((g) => [g.label, ...g.cells.map((v) => (v ? money(v, ctx.roundUp) : "")), money(g.rowTotal, ctx.roundUp)]);
    body.push(["Total", ...m.colTotals.map((v) => money(v, ctx.roundUp)), money(m.grandTotal, ctx.roundUp)]);
    autoTable(doc, {
      head,
      body,
      startY: 22,
      margin: { left: 8, right: 8 },
      styles: { fontSize: 7, cellPadding: 1.2, overflow: "linebreak", halign: "right" },
      columnStyles: { 0: { halign: "left" } },
      headStyles: { fillColor: [31, 77, 63], textColor: 255, fontStyle: "bold", halign: "right" },
      alternateRowStyles: { fillColor: [250, 248, 242] },
      footStyles: { fillColor: [230, 236, 232], textColor: 20, fontStyle: "bold" },
      didParseCell: (d) => { if (d.row.index === body.length - 1) d.cell.styles.fontStyle = "bold"; },
    });
  }
  doc.save(`booking-office-sales-${data.fiscalYearLabel.replace("/", "-")}-${ctx.rangeLabel.replace(/[^\w]+/g, "_")}.pdf`);
}

// ---------------------------------------------------------------------------
// Excel export — two sheets (Summary, Sales) as real .xlsx.
// ---------------------------------------------------------------------------
type XlsxCell = { value: string | number | null; type?: typeof String | typeof Number; fontWeight?: "bold"; format?: string; align?: "left" | "right" };
const CURRENCY_FMT = "£#,##0.00";

function matrixSheet(m: Matrix, bucketHeader: string): XlsxCell[][] {
  const header: XlsxCell[] = [
    { value: bucketHeader, type: String, fontWeight: "bold" },
    ...m.visible.map((loc) => ({ value: loc, type: String, fontWeight: "bold", align: "right" } as XlsxCell)),
    { value: "Total", type: String, fontWeight: "bold", align: "right" },
  ];
  const body: XlsxCell[][] = m.grid.map((g) => [
    { value: g.label, type: String },
    ...g.cells.map((v) => ({ value: v, type: Number, format: CURRENCY_FMT } as XlsxCell)),
    { value: g.rowTotal, type: Number, format: CURRENCY_FMT, fontWeight: "bold" },
  ]);
  const footer: XlsxCell[] = [
    { value: "Total", type: String, fontWeight: "bold" },
    ...m.colTotals.map((v) => ({ value: v, type: Number, format: CURRENCY_FMT, fontWeight: "bold" } as XlsxCell)),
    { value: m.grandTotal, type: Number, format: CURRENCY_FMT, fontWeight: "bold" },
  ];
  return [header, ...body, footer];
}

async function exportExcel(
  data: BOResponse,
  ctx: { measure: Measure; matrix: Matrix; summary: { items: { location: string; value: number }[]; total: number }; rangeLabel: string }
) {
  const summarySheet: XlsxCell[][] = [
    [{ value: `Booking office ticket sales — season ${data.fiscalYearLabel} by location (${ctx.measure})`, type: String, fontWeight: "bold" }, { value: null }],
    [{ value: "Location", type: String, fontWeight: "bold" }, { value: ctx.measure, type: String, fontWeight: "bold", align: "right" }],
    ...ctx.summary.items.map((it) => [
      { value: it.location, type: String } as XlsxCell,
      { value: it.value, type: Number, format: CURRENCY_FMT } as XlsxCell,
    ]),
    [{ value: "Total", type: String, fontWeight: "bold" }, { value: ctx.summary.total, type: Number, format: CURRENCY_FMT, fontWeight: "bold" }],
  ];
  const sheets = [
    { data: summarySheet, sheet: "Summary" },
    { data: matrixSheet(ctx.matrix, "Date/Month"), sheet: "Sales" },
  ];
  const fileName = `booking-office-sales-${data.fiscalYearLabel.replace("/", "-")}-${ctx.rangeLabel.replace(/[^\w]+/g, "_")}.xlsx`;
  const blob = await writeXlsxFile(sheets as never).toBlob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
